import _ from "lodash";
import * as React from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu, {
  KuikaDropdownMenuItemType
} from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import "./config-parameter-list.scss";

/**
 * The request parameters a generic-auth call sends: where each one goes, its key, and its
 * value.
 *
 * Generic Auth draws this twice — once for the token call and once for the refresh — and
 * both copies were the same eighty lines: an antd `Input.Group compact` holding a `Select`
 * and two `Input`s each pinned to `calc(100% / 3)`, a chevron and a bin drawn inline with
 * `stroke="black"`, and a `Menu` behind an `<a className="ant-dropdown-link">`. The joined
 * look is the only thing `Input.Group` was for, and a flex row with the inner borders
 * collapsed does it without the antd dependency or the width arithmetic.
 */

export interface IConfigParameter {
  id: number;
  /** `custom` is the editable one. Every other name pins its value — see `lockedValues`. */
  name: string;
  key: string;
  value: string;
  type: string;
}

export interface IConfigParameterAddOption {
  /** What the owner's add handler switches on. Kept as the string it already was. */
  key: string;
  label: string;
  disabled?: boolean;
}

export interface IConfigParameterListProps {
  parameters: IConfigParameter[];
  onChange: (parameters: IConfigParameter[]) => void;
  /**
   * The parameters the caller adds ready-made — `{username}`, `{token}`. Their value is the
   * point of them, so it is shown and locked rather than left for the user to retype.
   */
  lockedValues: Record<string, string>;
  addOptions: IConfigParameterAddOption[];
  onAdd: (key: string) => void;
  addLabel?: string;
}

const DESTINATIONS: KuikaSelectboxOption[] = [
  { value: "header", label: "Header" },
  { value: "query", label: "Query" },
  { value: "body", label: "Body" }
];

export const ConfigParameterList = ({
  parameters,
  onChange,
  lockedValues,
  addOptions,
  onAdd,
  addLabel = "Add parameters"
}: IConfigParameterListProps) => {
  const update = (index: number, patch: Partial<IConfigParameter>): void => {
    const next: IConfigParameter[] = _.cloneDeep(parameters);
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  const menuOptions: KuikaDropdownMenuItemType[] = addOptions.map(
    (option: IConfigParameterAddOption): KuikaDropdownMenuItemType => ({
      key: option.key,
      label: option.label,
      disabled: option.disabled,
      onClick: (): void => onAdd(option.key)
    })
  );

  return (
    <div className="kuika-config-parameters">
      {parameters.map((parameter: IConfigParameter, index: number) => (
        <div className="kuika-config-parameters__row" key={parameter.id}>
          <div className="kuika-config-parameters__group">
            <KuikaSelectbox
              size="md"
              value={parameter.type}
              options={DESTINATIONS}
              onChange={(value: string | number): void => update(index, { type: value.toString() })}
            />
            <KuikaInput
              size="md"
              placeholder="key"
              value={parameter.key}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => update(index, { key: e.target.value })}
            />
            {parameter.name === "custom" && (
              <KuikaInput
                size="md"
                placeholder="value"
                value={parameter.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => update(index, { value: e.target.value })}
              />
            )}
            {parameter.name !== "custom" && lockedValues[parameter.name] !== undefined && (
              <KuikaInput size="md" value={lockedValues[parameter.name]} disabled />
            )}
          </div>
          {/* The last parameter is what the call is for — there is nothing to send without
              one, so it has no bin. */}
          {parameters.length > 1 && (
            <button
              type="button"
              className="kuika-config-parameters__remove"
              aria-label={`Remove parameter ${parameter.key || index + 1}`}
              onClick={(): void =>
                onChange(parameters.filter((item: IConfigParameter): boolean => item.id !== parameter.id))
              }
            >
              <KuikaSvg name="trash" width={16} height={16} />
            </button>
          )}
        </div>
      ))}

      <KuikaDropdownMenu options={menuOptions} placement="bottomLeft">
        <KuikaButton type="text" size="sm" uppercase>
          {addLabel}
        </KuikaButton>
      </KuikaDropdownMenu>
    </div>
  );
};
